import React, { CSSProperties, Fragment, useEffect, useState } from "react";
import { Checkbox, ComboBoxItem } from "@aderant/aderant-react-components";
import { Messages } from "./Messages";
import { useDispatch, useSelector } from "react-redux";
import { FirmSettings, HitStatus } from "aderant-conflicts-models";
import { adminActions } from "state/actions/AdminActions";
import { FirmSettingsPageFooter } from "pages/FirmSettingsPage/FirmSettingsPageFooter";
import { getIsFirmSettingsPagePersisting } from "state/selectors";
import { RootState } from "MyTypes";
import { DataLoadedState, isAllPageDataLoadedOrNotErrored } from "state/reducers/adminReducers";
import { getAllHitStatuses } from "dataProviders/hitStatusList";
import { getReviewerOptions } from "state/selectors/adminSelectors";

const optionListContainerStyles: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "10px",
    paddingLeft: "6px",
    paddingRight: "6px",
    overflowY: "auto"
};

const optionHeaderContainerStyles: CSSProperties = {
    paddingLeft: "20px",
    paddingRight: "20px"
};

const styleBlockHeader: CSSProperties = {
    marginTop: "2rem",
    fontSize: "16px",
    fontWeight: "bolder"
};

export const ReviewerOptionsComponent = (): JSX.Element => {
    const dispatch = useDispatch();
    const pageName = FirmSettings.ReviewerOptionsDefinition.name;
    const isPersisting: boolean = useSelector((rootState: RootState) => getIsFirmSettingsPagePersisting(rootState, pageName));
    const isDirty = useSelector((rootState: RootState) => rootState.admin.isEdited?.[pageName] ?? false);
    const reviewerOptionsData = useSelector(getReviewerOptions);
    const isDataLoaded: DataLoadedState[] = useSelector((rootState: RootState) => rootState.admin.isLoaded?.[pageName] ?? []);
    const [hitSharedOptions, setHitSharedOptions] = useState<ComboBoxItem<HitStatus>[]>([]);
    const [statusControlOptions, setStatusControlOptions] = useState<ComboBoxItem<HitStatus>[]>([]);
    const [selectedHitShared, setSelectedHitShared] = useState<{ [key: string]: boolean }>({});
    const [selectedStatusControl, setSelectedStatusControl] = useState<{ [key: string]: boolean }>({});
    const [initialSelections, setInitialSelections] = useState<{
        hitShared: { [key: string]: boolean };
        statusControl: { [key: string]: boolean };
    }>({ hitShared: {}, statusControl: {} });

    function setIsDirty(isDirtyInput: boolean) {
        if (!!isDirty !== isDirtyInput) {
            dispatch(adminActions.setIsPageEdited({ pageName, isPageEdited: isDirtyInput }));
        }
    }

    useEffect(() => {
        async function fetchStatuses() {
            const hitShared = await getAllHitStatuses();
            const statusControl = await getAllHitStatuses();

            setHitSharedOptions(hitShared);
            setStatusControlOptions(statusControl);
            dispatch(adminActions.fetchReviewerOptions());
        }

        fetchStatuses();
    }, [dispatch]);

    // Populate default checkbox states based on reviewerOptionsData
    useEffect(() => {
        if (reviewerOptionsData) {
            const updatedHitShared: { [key: string]: boolean } = {};
            const updatedStatusControl: { [key: string]: boolean } = {};

            // Populate checkboxes with existing selections
            reviewerOptionsData.hitShared.forEach((option) => {
                updatedHitShared[option] = true;
            });

            reviewerOptionsData.statusControl.forEach((option) => {
                updatedStatusControl[option] = true;
            });

            setSelectedHitShared(updatedHitShared);
            setSelectedStatusControl(updatedStatusControl);

            // Store initial selections for comparison
            setInitialSelections({
                hitShared: { ...updatedHitShared },
                statusControl: { ...updatedStatusControl }
            });
        }
    }, [reviewerOptionsData]);

    useEffect(() => {
        // Check if current selections differ from the initially loaded ones
        const isHitSharedDirty = JSON.stringify(selectedHitShared) !== JSON.stringify(initialSelections.hitShared);
        const isStatusControlDirty = JSON.stringify(selectedStatusControl) !== JSON.stringify(initialSelections.statusControl);
        setIsDirty(isHitSharedDirty || isStatusControlDirty);
    }, [selectedHitShared, selectedStatusControl, initialSelections]);

    const handleCheckboxChange = (value: string, checked: boolean, type: "hitShared" | "statusControl") => {
        if (type === "hitShared") {
            setSelectedHitShared((prev) => ({
                ...prev,
                [value]: checked
            }));
        } else {
            setSelectedStatusControl((prev) => ({
                ...prev,
                [value]: checked
            }));
        }
    };

    const handleSave = () => {
        const selectedHitSharedToSave = Object.keys(selectedHitShared).filter((key) => selectedHitShared[key]);
        const selectedStatusControlToSave = Object.keys(selectedStatusControl).filter((key) => selectedStatusControl[key]);

        const transformedData = {
            hitShared: selectedHitSharedToSave,
            statusControl: selectedStatusControlToSave
        };

        // Dispatch the save action with both sets of selected checkboxes
        dispatch(
            adminActions.saveReviewerOptions({
                reviewerOptions: transformedData
            })
        );

        setIsDirty(false);
    };

    if (isAllPageDataLoadedOrNotErrored(isDataLoaded)) {
        return (
            <Fragment>
                <div className="hit-shared-block">
                    <div style={optionHeaderContainerStyles}>
                        <p style={styleBlockHeader}>{Messages.HIT_SHARED_HEADER.getMessage()}</p>
                        <p>{Messages.HIT_SHARED_SUBHEADER.getMessage()}</p>
                    </div>
                    <div style={optionListContainerStyles}>
                        {hitSharedOptions.map((option) => (
                            <Checkbox
                                key={option.value}
                                id={`checkbox-hitShared-${option.value}`}
                                label={`${option.displayName}`}
                                checked={option.value ? selectedHitShared[option.value] ?? false : false}
                                onChange={(e) => option.value && handleCheckboxChange(option.value, e.target.checked, "hitShared")}
                            />
                        ))}
                    </div>
                </div>
                <div className="status-control-block">
                    <div style={optionHeaderContainerStyles}>
                        <p style={styleBlockHeader}>{Messages.STATUS_CONTROL_HEADER.getMessage()}</p>
                        <p>{Messages.STATUS_CONTROL_SUBHEADER.getMessage()}</p>
                    </div>
                    <div style={optionListContainerStyles}>
                        {statusControlOptions.map((option) => (
                            <Checkbox
                                key={option.value}
                                id={`checkbox-statusControl-${option.value}`}
                                label={`${option.displayName}`}
                                checked={option.value ? selectedStatusControl[option.value] ?? false : false}
                                onChange={(e) => option.value && handleCheckboxChange(option.value, e.target.checked, "statusControl")}
                            />
                        ))}
                    </div>
                </div>

                <FirmSettingsPageFooter
                    disabled={!isDirty || isPersisting}
                    onCancel={() => {
                        setIsDirty(false);
                        setSelectedHitShared(initialSelections.hitShared);
                        setSelectedStatusControl(initialSelections.statusControl);
                    }}
                    onSave={handleSave}
                />
            </Fragment>
        );
    }

    return <></>;
};
